/**
 * commentDecorationPlugin — paints open comment anchors inside the editor.
 *
 * The editor is fed comments from outside (DraftEditor's data feed dispatches a
 * metadata transaction), because the plugin must not know about stores or
 * fetching. Every anchor goes through `resolveAnchor` — the same resolver the
 * `comment:jump` listener uses — so a highlight and a jump can never disagree.
 *
 * What is NEVER decorated: resolved comments, whole-document comments
 * (`sectionId === null`), and section-level comments (no text anchor at all).
 * Those have no range to paint; painting a guess would be a lie about where the
 * reviewer was looking.
 *
 * Mode matters. `single` is a section/questionnaire editor (or a one-segment
 * document): the doc holds no `segmentHeading` delimiters, so `resolveAnchor`
 * falls back to searching the whole doc — and another section's quote would
 * happily match here. The `currentSectionKey` filter is what stops that.
 * `composite` is a multi-segment document: sections ARE delimited, so any
 * comment whose section resolves to a real range may paint.
 */

import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorState, Transaction } from '@tiptap/pm/state'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { EditorView } from '@tiptap/pm/view'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import type { Comment } from '@/lib/comments-api'
import { findSectionRange, resolveAnchor } from '@/lib/commentAnchor'

// ===================
// Types
// ===================

/** The only comment fields a highlight depends on. */
export type CommentDecorationComment = Pick<
  Comment,
  'id' | 'sectionId' | 'startPos' | 'endPos' | 'quotedText' | 'status'
>

export interface CommentDecorationInput {
  comments: CommentDecorationComment[]
  focusedCommentId: string | null
}

export interface CommentDecorationPluginState {
  input: CommentDecorationInput
  decorations: DecorationSet
}

export interface CommentDecorationOptions {
  mode: 'composite' | 'single'
  /** Single mode: the only sectionId whose comments may decorate. */
  currentSectionKey: string | null
}

/** Internal meta asking for a re-resolve against the current doc. */
interface RecomputeMeta {
  recompute: true
}

type CommentDecorationMeta = CommentDecorationInput | RecomputeMeta

export const commentDecorationPluginKey = new PluginKey<CommentDecorationPluginState>(
  'commentDecoration',
)

/**
 * Transaction meta the DraftEditor's `onUpdate` skips, mirroring
 * `suggestion-update` / `selection-highlight-update`. Decoration traffic must
 * never mark the draft dirty.
 */
export const COMMENT_DECORATION_META = 'comment-decoration-update'

/**
 * Re-resolving anchors on every keystroke is wasted work — positions map
 * exactly for local edits. Resolution only has to catch up once typing settles.
 */
export const COMMENT_DECORATION_DEBOUNCE_MS = 300

const EMPTY_INPUT: CommentDecorationInput = { comments: [], focusedCommentId: null }

// ===================
// Decoration building
// ===================

function isRecompute(meta: CommentDecorationMeta): meta is RecomputeMeta {
  return 'recompute' in meta
}

function hasTextAnchor(comment: CommentDecorationComment): boolean {
  if (comment.quotedText != null && comment.quotedText.length > 0) return true
  return comment.startPos != null && comment.endPos != null
}

function buildDecorations(
  doc: ProseMirrorNode,
  input: CommentDecorationInput,
  options: CommentDecorationOptions,
): DecorationSet {
  const source = { state: { doc } }
  const decorations: Decoration[] = []

  for (const comment of input.comments) {
    if (comment.status !== 'open') continue
    if (comment.sectionId == null) continue
    if (!hasTextAnchor(comment)) continue

    if (options.mode === 'single') {
      if (options.currentSectionKey == null) continue
      if (comment.sectionId !== options.currentSectionKey) continue
    } else if (findSectionRange(source, comment.sectionId) == null) {
      // Composite doc, but this comment's section isn't in the document —
      // whole-doc fallback would quote-match a neighbouring section.
      continue
    }

    const range = resolveAnchor(source, comment)
    if (!range) continue
    if (range.from < 0 || range.to > doc.content.size || range.to <= range.from) continue

    decorations.push(
      Decoration.inline(range.from, range.to, {
        class:
          comment.id === input.focusedCommentId
            ? 'comment-highlight comment-highlight-focused'
            : 'comment-highlight',
        'data-comment-id': comment.id,
      }),
    )
  }

  return DecorationSet.create(doc, decorations)
}

// ===================
// Plugin
// ===================

export function createCommentDecorationPlugin(
  options: CommentDecorationOptions,
): Plugin<CommentDecorationPluginState> {
  let view: EditorView | null = null
  let recomputeTimer: ReturnType<typeof setTimeout> | null = null

  function cancelRecompute(): void {
    if (recomputeTimer === null) return
    clearTimeout(recomputeTimer)
    recomputeTimer = null
  }

  function scheduleRecompute(): void {
    cancelRecompute()
    recomputeTimer = setTimeout(() => {
      recomputeTimer = null
      if (!view) return
      const meta: RecomputeMeta = { recompute: true }
      view.dispatch(
        view.state.tr
          .setMeta(commentDecorationPluginKey, meta)
          .setMeta(COMMENT_DECORATION_META, true)
          .setMeta('addToHistory', false),
      )
    }, COMMENT_DECORATION_DEBOUNCE_MS)
  }

  return new Plugin<CommentDecorationPluginState>({
    key: commentDecorationPluginKey,
    state: {
      init: (): CommentDecorationPluginState => ({
        input: EMPTY_INPUT,
        decorations: DecorationSet.empty,
      }),
      apply: (
        tr: Transaction,
        value: CommentDecorationPluginState,
      ): CommentDecorationPluginState => {
        const meta = tr.getMeta(commentDecorationPluginKey) as CommentDecorationMeta | undefined

        if (meta) {
          cancelRecompute()
          const input = isRecompute(meta) ? value.input : meta
          return { input, decorations: buildDecorations(tr.doc, input, options) }
        }

        if (tr.docChanged) {
          // Map now (highlights track the text the user is editing), re-resolve
          // once the burst settles.
          scheduleRecompute()
          return {
            input: value.input,
            decorations: value.decorations.map(tr.mapping, tr.doc),
          }
        }

        return value
      },
    },
    view: (editorView: EditorView) => {
      view = editorView
      return {
        destroy: () => {
          cancelRecompute()
          view = null
        },
      }
    },
    props: {
      decorations(state: EditorState): DecorationSet {
        return commentDecorationPluginKey.getState(state)?.decorations ?? DecorationSet.empty
      },
    },
  })
}
